<script setup lang="ts">
import { computed } from 'vue'
import type { Filter, FilterValue } from '@/types/api/reports'
import ColorTagSelect from '@/components/lib/GwTable/ColorNote/ColorTagSelect.vue'
import ActionSequenceFilter from '@/components/lib/GwTable/ActionSequence/ActionSequenceFilter.vue'
import {
  type ActionSequence,
  getStreet,
} from '@/components/lib/GwTable/ActionSequence/actionSequenceUtils'
import { type GwBtnGroupProps } from '@/components/lib/GwBtnGroup/GwBtnGroup.vue'
import GwInput from '@/components/lib/GwInput.vue'
import type { TFilterEmitValue } from './utils'
import ItemBtnGroup from './ItemBtnGroup.vue'
import RemoveButton from './RemoveButton.vue'
import CardGroupesPicker from '../../../CardGroupesPicker/CardGroupesPicker.vue'
import Datepicker from '../../../DataTables/Filters/Datepicker/Datepicker.vue'
import { addTimeToDate } from './utils'
import SelectInput from './SelectInput.vue'
import SearchInput from './SearchInput.vue'
import GwDivider from '../../../lib/GwDivider/GwDivider.vue'
import RangeSelect from './RangeSelect.vue'

export type Props = {
  item: Filter
  hideRemoveBtn?: boolean
  onClick?: (...args: any[]) => void
  btnGroupBoxVariant?: GwBtnGroupProps['boxVariant']
  btnGroupDirection?: GwBtnGroupProps['direction']
  btnVariant?: GwBtnGroupProps['btnVariant']
}

const props = withDefaults(defineProps<Props>(), {
  btnGroupBoxVariant: 'box_row_wrap',
  btnGroupDirection: 'horizontal',
  btnVariant: 'btn_grey_white_primary',
})

export type PickerComponentEmits = {
  'update:filter': [Filter]
  'remove:filter': [Filter]
}

const emit = defineEmits<PickerComponentEmits>()

const handleUpdate = ({ value, is_active }: TFilterEmitValue) => {
  const item: Filter = {
    ...props.item,
    value,
    is_active,
  }

  emit('update:filter', item)
}

const field = computed(() => props.item.filter)
const value = computed(() => props.item.value)
const isActive = computed(() => props.item.is_active)
const uiType = computed(() => props.item.filter.filter_ui_type)
</script>
<template>
  <div
    class="f-column mh-0 mw-0 w-100"
    style="gap: var(--app-gap-2)"
    :data-tst="`${item.filter.field_name}_filter`"
  >
    <ItemBtnGroup
      v-if="uiType === 'SELECT'"
      :field
      :value
      :isActive
      :boxVariant="btnGroupBoxVariant"
      :direction="btnGroupDirection"
      :btnVariant
      @update:filterField="handleUpdate($event)"
    >
    </ItemBtnGroup>
    <GwInput
      v-else-if="uiType === 'INPUT'"
      :value="value as string"
      @input="(_e, value) => handleUpdate({ field: item.filter, is_active: isActive, value })"
    ></GwInput>
    <SelectInput
      v-else-if="uiType === 'INPUT_AND_SELECT'"
      :field
      :value="value as string"
      :isActive
      @update:filterField="handleUpdate($event)"
    />
    <SearchInput
      v-else-if="uiType === 'SELECT_WITH_SEARCH'"
      :field
      :isActive
      :value
      @update:filterField="handleUpdate($event)"
    />

    <RangeSelect
      v-else-if="uiType === 'RANGE_SELECT'"
      :from="value && (value as string[])[0]"
      :to="value && (value as string[])[1]"
      :field
      @update="
        ({ from, to }) =>
          handleUpdate({ field: item.filter, is_active: isActive, value: [from, to] })
      "
    />
    <CardGroupesPicker
      v-else-if="uiType === 'HAND'"
      rangeInputCssClass="rainput_variant_circle"
      style="
        min-width: clamp(1px, 28rem, 100vw);
        --custom-rainput-height: 0.75rem;
        --rtc-min-height: 2.5rem;
      "
      tableStyle="
      --rtc-clr-back-hover: var(--app-clr-primary-v2-light);
      --rtc-clr-back-active: var(--app-clr-primary-v2);
      --rtc-clr-hover: black;
      --rtc-clr-active: black;
      "
      :rangeInputStyle="{
        knob_1: `--clr-input-back: var(--clr-black-3); --clr-input-handle: var(--clr-primary-brand-2)`,
        knob_2: `--clr-input-back: var(--clr-primary); --clr-input-handle: var(--clr-primary-brand-2)`,
      }"
      :groups="value as string[]"
      @update:groups="
        group => handleUpdate({ field: item.filter, is_active: isActive, value: group })
      "
    />
    <Datepicker
      v-else-if="uiType === 'DATETIME'"
      :hasShortcuts="true"
      :visibleShortcutOptions="[
        'yesterday', //
        'last_3_days',
        'last_week',
        'last_month',
        'last_3_months',
      ]"
      :selectedDate="
        value ? { date_from: (value as string[])[0], date_to: (value as string[])[1] } : undefined
      "
      @update="
        newDate => {
          const { date_from, date_to } = addTimeToDate(newDate)
          handleUpdate({
            field: item.filter,
            is_active: isActive,
            value: [date_from, date_to],
          })
        }
      "
    />
    <ColorTagSelect
      v-else-if="uiType === 'COLOR_TAG'"
      multiValue
      :colorHex="(value || []) as string[]"
      :variant="btnGroupDirection === 'vertical' ? 'double_row' : 'single_row'"
      :style="btnGroupDirection === 'horizontal' && '--icon-size: 2.5rem'"
      @update:colors="val => handleUpdate({ field, value: val, is_active: isActive })"
    />
    <ActionSequenceFilter
      v-else-if="uiType === 'ACTIONS_SEQUENCE'"
      :actionsSequence="(value || [[]]) as ActionSequence"
      :street="getStreet(item.filter.field_name)"
      :variant="btnGroupDirection === 'vertical' ? 'single' : 'split'"
      @update:value="
        val =>
          handleUpdate({
            field: item.filter,
            value: val as unknown as FilterValue,
            is_active: isActive,
          })
      "
    />

    <GwDivider
      v-if="!hideRemoveBtn"
      variant="divider_horizontal"
    />

    <RemoveButton
      v-if="!hideRemoveBtn"
      class="mx-auto"
      @remove:filter="$emit('remove:filter', item)"
    />
  </div>
</template>
